import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ticket } from 'lucide-react';
import { Card } from '@/components/ui/card.js';
import { Input } from '@/components/ui/input.js';
import { Button } from '@/components/ui/button.js';
import {
  redeemCoupon,
  invalidateClaimState,
  PROMO_QUERY_KEYS,
  bigIntish,
  type RedeemCouponResult,
} from '@/features/promo/api.js';
import { WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import { mapApiError, ApiError } from '@/lib/api/error.js';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';

/**
 * Codes this screen can produce, mapped to keys in its OWN (`promo`) namespace.
 * Anything absent falls through to `mapApiError`, which looks the code up in the
 * `errors` namespace and, when that catalog has no row either, shows the
 * localized generic `errors:unknown` (`lib/api/error.js` passes it as
 * `defaultValue`). So the failure this map prevents is no longer a raw
 * `PROMO-NNN` leaking to a player — it is a redemption refused for a reason the
 * player could have acted on being reported as "something went wrong".
 *
 * That is worth a map because every code below is the player's own problem to
 * fix: the coupon expired, it was already used, the cap is full, they are not
 * eligible yet. A generic tells them to contact support for something they
 * could have resolved in five seconds, and support then pays for it.
 *
 * Exported so `RedeemCouponCard.errorKeys.test.ts` can prove every value here
 * resolves in all seven locales — a key that resolves nowhere silently degrades
 * back to that same generic.
 */
export const COUPON_ERROR_KEYS: Record<string, string> = {
  'PROMO-501': 'redeem.error.inactive',
  'PROMO-502': 'redeem.error.notYetValid',
  'PROMO-503': 'redeem.error.expired',
  'PROMO-504': 'redeem.error.capReached',
  'PROMO-506': 'redeem.error.alreadyRedeemed',
  'PROMO-101': 'redeem.error.promotionInactive',
  // The campaign's own limits, which the redeem path now enforces: the coupon
  // lives inside the promotion's frame rather than beside it, so a code with
  // uses left is refused once the CAMPAIGN's budget is spent (103) or once this
  // member holds as many claims on it as it allows (104). Both are the
  // promotion's word, not the coupon's, so neither may borrow
  // `redeem.error.capReached` / `alreadyRedeemed` — a player told "this coupon
  // is used up" would go looking for another code that cannot work either.
  'PROMO-103': 'redeem.error.campaignFull',
  'PROMO-104': 'redeem.error.alreadyClaimedPromotion',
  // A coupon bound to a `welcome` promotion is refused PROMO-030 once the member
  // already holds a welcome grant, from any of the three grant paths. The key
  // lives in `promo.json`, not `errors.json`, so it resolves against this card's
  // own namespace like every line above it.
  'PROMO-030': 'errors.PROMO-030',
  // A code bound to a cashback / turnover_rebate campaign. svc-promo
  // refuses it at create AND at redeem, but codes issued before that gate are
  // live rows a player can still type in. Mapped rather than left generic for
  // the reason PROMO-102 is generic, read the other way round: the player can
  // do nothing about a misconfigured campaign, so the useful message is the one
  // that stops them retrying the same code and sends them to support.
  'PROMO-069': 'redeem.error.notGrantable',
};

/**
 * The `promo`-namespace key for a refusal code, or nothing.
 *
 * The own-property check is the whole function. `code` is read verbatim out of
 * the refusal body (`apiErrorFromResponse`), so a plain index reaches
 * `Object.prototype`: `constructor` resolves to a function and `toString` to
 * one too, and either is then handed to `t()` as if it were a catalog key. Same
 * defect the error catalog closed for i18next lookups in #438.
 */
export function couponErrorKey(code: string | undefined): string | undefined {
  if (typeof code !== 'string' || !Object.hasOwn(COUPON_ERROR_KEYS, code)) return undefined;
  return COUPON_ERROR_KEYS[code];
}

export function RedeemCouponCard({ currency }: { currency: string }) {
  const { t } = useTranslation('promo');
  const formatMoney = useFormatMoney();
  const qc = useQueryClient();
  const [code, setCode] = useState('');

  const mutation = useMutation({
    mutationFn: () => redeemCoupon({ code: code.trim() }),
    onSuccess: (result: RedeemCouponResult) => {
      invalidateClaimState(qc);
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      void qc.invalidateQueries({ queryKey: PROMO_QUERY_KEYS.list({ status: 'active' }) });
      const minor = bigIntish(result.claim.grantedAmount ?? result.claim.amount);
      if (minor > 0n) {
        toast.success(
          t('redeem.success', { amount: formatMoney(minor, result.claim.currency ?? currency) }),
        );
      } else {
        toast.success(t('redeem.received'));
      }
      setCode('');
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const key = couponErrorKey(err.code);
        if (key) return toast.error(t(key));
        if (err.status === 404) return toast.error(t('redeem.error.notFound'));
      }
      toast.error(mapApiError(err, t));
    },
  });

  const trimmed = code.trim();
  const submit = () => {
    if (!trimmed || mutation.isPending) return;
    mutation.mutate();
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Ticket size={16} className="text-[var(--text-secondary)]" />
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('redeem.title')}
        </h3>
      </div>

      <Card className="flex flex-col gap-3 p-4">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={t('redeem.placeholder')}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          disabled={mutation.isPending}
        />
        <Button fullWidth onClick={submit} disabled={!trimmed || mutation.isPending}>
          {mutation.isPending ? t('redeem.submitting') : t('redeem.submit')}
        </Button>
      </Card>
    </section>
  );
}
